import React, { useState, useEffect, ReactNode } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  SelectChangeEvent,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

import { Add as AddIcon, Remove, Close } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';

import { EXPERT_ANSWERS_TYPES, ELEMENT_TYPE } from './constants';
import { addNewExpertAnswer } from '../../api/ExpertAnswers';

const initialContent = {
  id: uuidv4(),
  type: '',
  allowedInReport: true,
  showReportCta: false,
  reportCtaType: 'default',
  addToReportType: 'default'
};

const initialStepsToRecreate = {
  id: uuidv4(),
  header: '',
  content: []
};

interface AddProps {
  showAddDialog: boolean;
  closeAddDialog: () => void;
  handleSubmitRefetch: () => void;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}>
      {value === index && children}
    </Box>
  );
};

TabPanel.defaultProps = {
  children: null
};

interface ElementRendererProps {
  content: any;
  index: number;
  onChange: (
    // eslint-disable-next-line no-unused-vars
    index: number,
    // eslint-disable-next-line no-unused-vars
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => void;
}

const ReportFields: React.FC<ElementRendererProps> = ({ content, index, onChange }) => {
  return (
    <Box display='flex' justifyContent='space-between' gap={1}>
      <FormControl variant='outlined' fullWidth size='small'>
        <InputLabel>Allowed In Report</InputLabel>
        <Select
          name='allowedInReport'
          label='Allowed In Report'
          required
          value={content.allowedInReport}
          onChange={e => onChange(index, e)}>
          <MenuItem value='true'>True</MenuItem>
          <MenuItem value='false'>False</MenuItem>
        </Select>
      </FormControl>
      <FormControl variant='outlined' fullWidth size='small'>
        <InputLabel>Show Report Button</InputLabel>
        <Select
          name='showReportCta'
          label='Show Report Button'
          required
          value={content.showReportCta}
          onChange={e => onChange(index, e)}>
          <MenuItem value='false'>False</MenuItem>
          <MenuItem value='true'>True</MenuItem>
        </Select>
      </FormControl>
      <FormControl variant='outlined' fullWidth size='small'>
        <InputLabel>Report Button Type</InputLabel>
        <Select
          name='reportCtaType'
          label='Report Button Type'
          required
          value={content.reportCtaType}
          onChange={e => onChange(index, e)}>
          <MenuItem value='default'>Default</MenuItem>
          <MenuItem value='small'>Small</MenuItem>
        </Select>
      </FormControl>
      <FormControl variant='outlined' fullWidth size='small'>
        <InputLabel>Add To Report Type</InputLabel>
        <Select
          name='addToReportType'
          label='Add To Report Type'
          required
          value={content.addToReportType}
          onChange={e => onChange(index, e)}>
          <MenuItem value='default'>Default</MenuItem>
          <MenuItem value='partial'>Partial</MenuItem>
          <MenuItem value='individual'>Individual</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

const ElementRenderer: React.FC<ElementRendererProps> = ({ content, index, onChange }) => {
  switch (content.type) {
    case ELEMENT_TYPE.PARAGRAPH:
      return (
        <>
          <TextField
            name='paragraph'
            label='Paragraph'
            variant='outlined'
            size='small'
            value={content.paragraph || ''}
            onChange={e => onChange(index, e)}
            fullWidth
          />
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    case ELEMENT_TYPE.HR:
      return (
        <>
          <TextField
            name='styles'
            label='Horizontal Rule'
            variant='outlined'
            size='small'
            value={content.styles || ''}
            onChange={e => onChange(index, e)}
            fullWidth
          />
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    case ELEMENT_TYPE.HEADER_1:
      return (
        <>
          <TextField
            name='header1'
            label='Header 1'
            variant='outlined'
            size='small'
            value={content.header1 || ''}
            onChange={e => onChange(index, e)}
            fullWidth
          />
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    case ELEMENT_TYPE.HEADER_2:
      return (
        <>
          <TextField
            name='header2'
            label='Header 2'
            variant='outlined'
            size='small'
            value={content.header2 || ''}
            onChange={e => onChange(index, e)}
            fullWidth
          />
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    case ELEMENT_TYPE.SPACE:
      return (
        <>
          <TextField
            name='styles'
            label='Space'
            variant='outlined'
            size='small'
            value={content.styles || ''}
            onChange={e => onChange(index, e)}
            fullWidth
          />
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    case ELEMENT_TYPE.DATA_TABLE:
      return (
        <TextField
          multiline
          rows={4}
          name='dataCall'
          label='Data Table API Payload'
          variant='outlined'
          size='small'
          value={content.dataCall || ''}
          onChange={e => onChange(index, e)}
          fullWidth
        />
      );
    case ELEMENT_TYPE.NORMAL_TABLE:
      return (
        <>
          <Button variant='outlined' component='label' size='small' fullWidth>
            Upload Excel File
            <input
              type='file'
              accept='.xlsx,.xls,.csv'
              hidden
              onChange={e => onChange(index, e)}
              name='excelFile'
            />
          </Button>
          {content.excelFileName && (
            <Typography variant='body2' mt={1}>
              Uploaded: {content.excelFileName}
            </Typography>
          )}
          {content.normalTable && content.normalTable.headerRow && content.normalTable.dataRows && (
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {content.normalTable.headerRow.map((header: any) => (
                      <TableCell key={header.id}>{header.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {content.normalTable.dataRows.map((row: any) => (
                    <TableRow key={row.id}>
                      {row.cells.map((cell: any) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ReportFields content={content} index={index} onChange={onChange} />
        </>
      );
    default:
      return null;
  }
};

const Add: React.FC<AddProps> = ({ showAddDialog, closeAddDialog, handleSubmitRefetch }) => {
  const defaultFormValues = {
    title: '',
    description: '',
    imageUrl: '',
    author: '',
    type: '',
    content: [initialContent],
    searchPrinciple: '',
    stepsToRecreate: [initialStepsToRecreate]
  };

  const defaultErrors = {
    title: '',
    description: '',
    imageUrl: '',
    author: '',
    type: '',
    content: [{}],
    searchPrinciple: '',
    stepsToRecreate: [{}]
  };

  const [formValues, setFormValues] = useState(defaultFormValues);

  const [errors, setErrors] = useState(defaultErrors);
  const [errorMessage, setErrorMessage] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [dragOverIndex, setDragOverIndex] = useState(-1);

  useEffect(() => {
    setFormValues(defaultFormValues);
    setErrors(defaultErrors);
    setErrorMessage('');
  }, [showAddDialog]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setFormValues(prevState => ({
      ...prevState,
      [name!]: value
    }));
  };

  const handleExcelUpload = (
    file: File,
    index: number,
    setContentFormValues: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const reader = new FileReader();
    reader.onload = (evt: any) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Transform the data
      const [headerRowRaw = [], ...dataRows] = json;
      const headerRow: string[] = Array.isArray(headerRowRaw) ? (headerRowRaw as string[]) : [];

      const transformed = {
        headerRow: headerRow.map((label: string) => ({
          id: uuidv4(),
          label
        })),
        dataRows: (dataRows as any[]).map((row: any[]) => ({
          id: uuidv4(),
          cells: row.map((value: string) => ({
            id: uuidv4(),
            value
          }))
        }))
      };

      setContentFormValues((prev: typeof formValues) => {
        const content = prev.content.map((item: any, idx: number) =>
          idx === index ? { ...item, normalTable: transformed } : item
        );
        return { ...prev, content };
      });
    };
    reader.readAsBinaryString(file);
  };

  const handleUpdateChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value, files } = e.target as any;
    if (name === 'excelFile' && files && files[0]) {
      handleExcelUpload(files[0], index, setFormValues);
      return;
    }
    setFormValues(prev => {
      const content = prev.content.map((item, idx) => {
        if (idx === index) {
          if (name === 'allowedInReport' || name === 'showReportCta') {
            return { ...item, [name]: value === 'true' };
          }
          return { ...item, [name!]: value };
        }
        return item;
      });
      return { ...prev, content };
    });
  };

  const getDefaultContentByType = (type: string) => {
    const base = {
      id: uuidv4(),
      type,
      allowedInReport: true,
      showReportCta: false,
      reportCtaType: 'default',
      addToReportType: 'default'
    };

    switch (type) {
      case ELEMENT_TYPE.PARAGRAPH:
        return { ...base, paragraph: '' };
      case ELEMENT_TYPE.HR:
        return { ...base, styles: `{ "my": 6, "borderColor": 'gray.300' }` };
      case ELEMENT_TYPE.HEADER_1:
        return { ...base, header1: '' };
      case ELEMENT_TYPE.HEADER_2:
        return { ...base, header2: '' };
      case ELEMENT_TYPE.SPACE:
        return { ...base, styles: `{ "height": 20 }` };
      case ELEMENT_TYPE.DATA_TABLE:
        return {
          ...base,
          allowedInReport: false,
          dataCall: `{
            "apiType": "generic",
            "dataTableSource": ["us"],
            "payload": 
          }`
        };
      default:
        return { ...base };
    }
  };

  const handleAddUpdate = () => {
    setFormValues(prevState => ({
      ...prevState,
      content: [
        ...prevState.content,
        {
          id: uuidv4(),
          type: '',
          allowedInReport: true,
          showReportCta: false,
          reportCtaType: 'default',
          addToReportType: 'default'
        }
      ]
    }));
    setErrors(prevState => ({
      ...prevState,
      content: [...prevState.content, {}]
    }));
  };

  const handleRemoveUpdate = (index: number) => {
    const updates = formValues.content.filter((_, i) => i !== index);
    setFormValues({ ...formValues, content: updates });
    const updatesErrors = errors.content.filter((_, i) => i !== index);
    setErrors({ ...errors, content: updatesErrors });
  };

  const handleAddingNewArticle = async (payload: any) => {
    try {
      const result = await addNewExpertAnswer(payload);

      if (result?.status !== 200) {
        return;
      }

      handleSubmitRefetch();
      closeAddDialog();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const content = formValues.content.map((item: any) => {
      if (item.type === ELEMENT_TYPE.HR || item.type === ELEMENT_TYPE.SPACE) {
        let { styles } = item;
        if (typeof styles === 'string') {
          try {
            styles = styles.trim() ? JSON.parse(styles) : {};
          } catch {
            styles = {};
          }
        }
        return { ...item, styles };
      }
      if (item.type === ELEMENT_TYPE.DATA_TABLE) {
        let { dataCall } = item;
        if (typeof dataCall === 'string') {
          try {
            dataCall = dataCall.trim() ? JSON.parse(dataCall) : {};
          } catch {
            dataCall = {};
          }
        }
        return { ...item, dataCall };
      }
      return item;
    });

    const payload = {
      ...formValues,
      content
    };

    await handleAddingNewArticle(payload);
  };

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
  };

  // Update element type in content array immutably and efficiently
  const handleElementTypeChange = (index: number, e: SelectChangeEvent) => {
    const { value } = e.target;
    setFormValues(prev => {
      const content = prev.content.map((item, idx) => {
        if (idx === index) {
          const defaults = getDefaultContentByType(value);
          // Only keep the original id, and use the new defaults for the rest
          return { ...defaults, id: item.id, type: value };
        }
        return item;
      });
      return { ...prev, content };
    });
  };

  // Drag and drop handlers using functional updates for safety
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    setFormValues(prev => {
      if (draggingIndex === -1 || draggingIndex === index) return prev;
      const content = [...prev.content];
      const [removed] = content.splice(draggingIndex, 1);
      content.splice(index, 0, removed);
      return { ...prev, content };
    });
    setDraggingIndex(-1);
    setDragOverIndex(-1);
  };

  const handleDragLeave = () => setDragOverIndex(-1);

  const handleDragEnd = () => {
    setDraggingIndex(-1);
    setDragOverIndex(-1);
  };

  return (
    <Dialog open={showAddDialog} onClose={closeAddDialog} fullScreen>
      <DialogContent sx={{ p: 1 }}>
        <form onSubmit={handleSubmit}>
          <Tabs value={tabIndex} onChange={handleTabChange} centered>
            <Tab label='Content' />
            <Tab label='Steps To Recreate' />
            <IconButton
              aria-label='close'
              onClick={closeAddDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme => theme.palette.grey[500]
              }}>
              <Close />
            </IconButton>
          </Tabs>
          <TabPanel value={tabIndex} index={0}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                my: 1,
                overflowY: 'auto',
                maxHeight: '82vh'
              }}>
              <TextField
                size='small'
                name='title'
                label='Title'
                variant='outlined'
                required
                value={formValues.title}
                onChange={handleInputChange}
                error={Boolean(errors.title)}
                helperText={errors.title}
              />
              <TextField
                size='small'
                name='description'
                label='Description'
                variant='outlined'
                required
                value={formValues.description}
                onChange={handleInputChange}
                error={Boolean(errors.description)}
                helperText={errors.description}
                multiline
                rows={2}
              />
              <Box display='flex' justifyContent='space-between' gap={1}>
                <TextField
                  fullWidth
                  size='small'
                  name='imageUrl'
                  label='Image URL'
                  variant='outlined'
                  required
                  value={formValues.imageUrl}
                  onChange={handleInputChange}
                  error={Boolean(errors.imageUrl)}
                  helperText={errors.imageUrl}
                />
                <TextField
                  fullWidth
                  size='small'
                  name='author'
                  label='Author'
                  required
                  variant='outlined'
                  value={formValues.author}
                  onChange={handleInputChange}
                  error={Boolean(errors.author)}
                  helperText={errors.author}
                />
                <FormControl variant='outlined' fullWidth size='small'>
                  <InputLabel>Type *</InputLabel>
                  <Select
                    name='type'
                    label='Type'
                    required
                    value={formValues.type}
                    onChange={handleInputChange}
                    error={Boolean(errors.type)}>
                    {Object.entries(EXPERT_ANSWERS_TYPES).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.type && (
                    <Typography variant='body2' color='error'>
                      {errors.type}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <Box
                id='content-list'
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                onDragOver={e => e.preventDefault()}>
                {formValues.content.map((content, index) => (
                  <Box
                    key={content.id}
                    sx={{
                      border: '1px dashed #ccc',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 1,
                      gap: 1,
                      opacity: draggingIndex === index ? 0.5 : 1,
                      background: dragOverIndex === index ? '#f0f0f0' : 'inherit',
                      cursor: 'move'
                    }}
                    draggable
                    onDragStart={() => setDraggingIndex(index)}
                    onDragOver={e => handleDragOver(e, index)}
                    onDrop={() => handleDrop(index)}
                    onDragLeave={handleDragLeave}
                    onDragEnd={handleDragEnd}>
                    <Typography variant='h6' mb={1}>
                      Content {index + 1}
                    </Typography>
                    <FormControl variant='outlined' size='small'>
                      <InputLabel>Element Type</InputLabel>
                      <Select
                        name='content-type'
                        label='Element Type'
                        value={content.type}
                        onChange={e => handleElementTypeChange(index, e)}
                        error={Boolean(errors.type)}>
                        {Object.keys(ELEMENT_TYPE).map(key => (
                          <MenuItem
                            key={key}
                            value={ELEMENT_TYPE[key as keyof typeof ELEMENT_TYPE]}>
                            {ELEMENT_TYPE[key as keyof typeof ELEMENT_TYPE]}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.type && (
                        <Typography variant='body2' color='error'>
                          {errors.type}
                        </Typography>
                      )}
                    </FormControl>
                    <ElementRenderer
                      content={content}
                      index={index}
                      onChange={handleUpdateChange}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        variant='contained'
                        color='error'
                        size='small'
                        startIcon={<Remove />}
                        onClick={() => handleRemoveUpdate(index)}
                        disabled={formValues?.content?.length < 2}>
                        Remove Content
                      </Button>
                      {index === formValues.content.length - 1 && (
                        <Button
                          variant='contained'
                          color='primary'
                          size='small'
                          startIcon={<AddIcon />}
                          onClick={() => handleAddUpdate()}>
                          Add Content
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
              <TextField
                size='small'
                name='searchPrinciple'
                label='Search Principle'
                variant='outlined'
                value={formValues.searchPrinciple}
                onChange={handleInputChange}
                error={Boolean(errors.searchPrinciple)}
                helperText={errors.searchPrinciple}
                multiline
                rows={3}
              />
            </Box>
          </TabPanel>
          <Box mt={1} textAlign='center'>
            {errorMessage.trim().length > 0 && (
              <Typography color='#d32f2f'>{errorMessage}</Typography>
            )}
            <Button type='submit' variant='contained' color='primary' size='small'>
              Submit (Add)
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Add;
